import type { ReactElement } from "react";
import { ClipboardList, LayoutGrid } from "lucide-react";
import { Badge, Button, EmptyState, Stack, Text } from "@ux/primitives";
import { DataTable, type DataColumn, type DataTableSelection } from "@ux/compositions";
import type { SageOfficeView } from "../sage-offices/types";
import type { WorkspaceView } from "./types";

export interface WorkspacesListProps {
    readonly rows: readonly WorkspaceView[];
    readonly officeIndex: ReadonlyMap<string, SageOfficeView>;
    readonly selectedId: string | null;
    readonly onSelect: (row: WorkspaceView) => void;
    /**
     * Optional jump-to-inventory action. When provided, an "Inventory"
     * button renders on each active row so a campaign user can start
     * walking that workspace without first opening it for edit.
     */
    readonly onStartInventory?: (workspaceId: string) => void;
    readonly isLoading: boolean;
    readonly error: string | null;
    readonly selection?: DataTableSelection;
}

export function WorkspacesList({
    rows,
    officeIndex,
    selectedId,
    onSelect,
    onStartInventory,
    isLoading,
    error,
    selection,
}: WorkspacesListProps): ReactElement {
    const baseColumns: DataColumn<WorkspaceView>[] = [
        {
            id: "name",
            header: "Workspace",
            render: (row) => {
                const office = officeIndex.get(row.sageOfficeId);
                // Friendly name (when present) goes between the bold
                // name and the muted Type · Office line. Search results
                // for "Collaboration" should make it obvious *why* a
                // row matched ("I.T. Collaboration Room") rather than
                // hiding the alias inside the secondary metadata. We
                // keep it on its own line because friendly names can
                // run up to 100 chars and inlining would wrap badly
                // on phone.
                const friendly =
                    row.friendlyName !== null &&
                    row.friendlyName !== undefined &&
                    row.friendlyName !== ""
                        ? row.friendlyName
                        : null;
                return (
                    <Stack gap={1}>
                        <Text role="body" weight="semibold">
                            {row.name}
                        </Text>
                        {friendly !== null ? <Text role="muted">{friendly}</Text> : null}
                        <Text role="muted">
                            {row.type}
                            {office !== undefined ? ` · ${office.name}` : ""}
                        </Text>
                    </Stack>
                );
            },
        },
        {
            id: "status",
            header: "Status",
            width: "110px",
            render: (row) =>
                row.isDeleted ? <Badge tone="danger">Deleted</Badge> : <Badge tone="ok">Active</Badge>,
        },
    ];

    const columns: readonly DataColumn<WorkspaceView>[] =
        onStartInventory === undefined
            ? baseColumns
            : [
                  ...baseColumns,
                  {
                      id: "inventory",
                      header: "",
                      width: "140px",
                      render: (row) =>
                          row.isDeleted ? null : (
                              <div onClick={(e) => e.stopPropagation()}>
                                  <Button
                                      variant="ghost"
                                      size="sm"
                                      leadingIcon={<ClipboardList size={14} />}
                                      onClick={() => onStartInventory(row.id)}
                                  >
                                      Inventory
                                  </Button>
                              </div>
                          ),
                  },
              ];

    if (isLoading && rows.length === 0) {
        return <EmptyState icon={<LayoutGrid size={28} />} title="Loading workspaces…" />;
    }
    if (error !== null) {
        return (
            <EmptyState
                icon={<LayoutGrid size={28} />}
                title="Could not load workspaces"
                description={error}
            />
        );
    }

    return (
        <DataTable<WorkspaceView>
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            selectedKey={selectedId}
            onSelect={onSelect}
            isRowMuted={(row) => row.isDeleted}
            {...(selection !== undefined ? { selection } : {})}
            emptyContent={
                <EmptyState
                    icon={<LayoutGrid size={28} />}
                    title="No workspaces yet"
                    description="Create the first workspace from the toolbar."
                />
            }
        />
    );
}
